import { Box, Grid, Paper, Typography, Tabs, Tab } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import WeeklyRevenueChart from "./DailyRevenueBarChart";
import MonthlyActivityCard from "./MonthlyActivityDonut";
import RevenueChart from "./RevenueTrendChart";
import WSDataGridNew, {
  IColumns,
} from "../../../Components/DataGrid/WSDataGridNew";
import { IHeaderRows } from "../../../Components/Filter/WSHeaderFilter";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import { getLocalValue, LoacalVariables, updateMainHeader } from "../../../commonFunctions/CommonFunctions";
import WSDashboardCard from "../../../Components/CardListing/WSDashboardCard";
import { WSIcons } from "../../../commonFunctions/iIcons";
import { TimePeriodSelector } from "../../../Components/TimePeriodSelector/TimePeriodSelector";
import axios from 'axios';
import RevenueRecentOrders from "./RevenueRecentOrders";
import AddIcon from "@mui/icons-material/Add";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import http from "../../../commonFunctions/httpClient";
interface SummaryCounts {
  totalRevenue: number;
  paymentsReceived: number;
  homeCollectionCharges: number;
  totalServiceFee: number;
}


// Define the API response type
interface RecentOrder {
  id: string;
  labId: string;
  collectionType: string;
  totalAmount: number;
  orderNumber: string;
  paymentMode: string;
  paymentStatus: string;
  status: string;
  createdAt: string;
  collectionFee: number;
  serviceFee: number;
  orderdate: string;
}
export default function RevenuePaymentsDashboard() {
  const [summaryCounts, setSummaryCounts] = useState<SummaryCounts>({
    totalRevenue: 0,
    paymentsReceived: 0,
    homeCollectionCharges: 0,
    totalServiceFee: 0
  });
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [labId, setLabId] = useState<string>("");

    useEffect(() => {
      const userId = getLocalValue(LoacalVariables.UserId);
      setLabId(userId || "");
    }, []);
  
  // Fetch summary counts
  const fetchSummaryCounts = useCallback(async (): Promise<void> => {
    if (!labId) {
      setSummaryLoading(false);
      return;
    }

    try {
      console.log("Fetching summary counts for labId:", labId);
      const response: any = await http.get(`lab/LabDashBoard/${labId}`);
      console.log("Summary response:", response);
      
      const apiData = response.data || response || {};
      const recentOrders = apiData.recentOrders || [];
      
      const counts = {
        totalRevenue: apiData.earnings || 0,
        paymentsReceived: recentOrders.filter((order: RecentOrder) => order.paymentStatus === "Paid").length,
        homeCollectionCharges: recentOrders.reduce((sum: number, order: RecentOrder) => sum + (order.collectionFee || 0), 0),
        totalServiceFee: recentOrders.reduce((sum: number, order: RecentOrder) => sum + (order.serviceFee || 0), 0),
      };
      
      setSummaryCounts(counts);
    } catch (err: any) {
      console.error("Summary fetch error:", err);
      setSummaryCounts({
        totalRevenue: 0,
        paymentsReceived: 0,
        homeCollectionCharges: 0,
        totalServiceFee: 0,
      });
    } finally {
      setSummaryLoading(false);
    }
  }, [labId]);

  useEffect(() => {
    fetchSummaryCounts();
  }, [fetchSummaryCounts]);

  
  return (
    <Grid sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              borderRadius: "16px",
              border: "1px solid #E5E5E5",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={10}>
                <Typography variant="h6" style={{ color: "#022219", fontWeight: 400 }}>
                  Revenue & Payments
                </Typography>
                <Typography variant="caption" style={{ color: "#828584", fontWeight: 400 }}>
                  Track payments, revenue trends, and manage transactions
                </Typography>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TimePeriodSelector selectedPeriod="This month" />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Dashboard Cards - Now populated with real data */}
      <Grid item sm={3}>
          <WSDashboardCard
            title="Total Revenue"
            value={summaryCounts.totalRevenue.toLocaleString()}
            percentage="+2.5%"
            bottomIcon={<TrendingUpIcon />}
            icon={<ShoppingCartIcon sx={{ color: "#098963" }} />}
            onClick={() => console.log("Card clicked")}
          />
        </Grid>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Payments Received"
            value={summaryCounts.paymentsReceived.toString()}
            percentage="+2.5%"
            bottomIcon={<TrendingUpIcon />}
            icon={<WSIcons.InStockIcon sx={{ color: "#098963" }} />}
            onClick={() => console.log("Card clicked")}
          />
        </Grid>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Home Collection Charges"
            value={summaryCounts.homeCollectionCharges.toLocaleString()}
            percentage="+2.5%"
            bottomIcon={<TrendingUpIcon />}
            icon={<WSIcons.OutOfStockIcon sx={{ color: "red" }} />}
            onClick={() => console.log("Card clicked")}
          />
        </Grid>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Total Service Fee"
            value={summaryCounts.totalServiceFee.toLocaleString()}
            percentage="+2.5%"
            bottomIcon={<TrendingUpIcon />}
            icon={<ShoppingCartIcon sx={{ color: "#098963" }} />}
            onClick={() => console.log("Card clicked")}
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, borderRadius: "16px", border: "1px solid #E5E5E5" }}>
            <RevenueChart />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: "16px", border: "1px solid #E5E5E5" }}>
            <MonthlyActivityCard />
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper sx={{ mt: 3, p: 2, borderRadius: "16px", border: "1px solid #E5E5E5" }}>
            <Typography fontWeight={600}>Daily Revenue This Week</Typography>
            <WeeklyRevenueChart />
          </Paper>
        </Grid>
      </Grid>
<Grid container spacing={2}>
  <Grid item xs={12}>
    <RevenueRecentOrders/>
  </Grid>
</Grid>
     
    </Grid>
  );
}
